import { LoginOutlined } from '@ant-design/icons';
import React from 'react';
import styled from 'styled-components';

interface ContainerProps {
  $visible: boolean;
}

const Container = styled.div<ContainerProps>`
  position: absolute;
  flex-flow: column;
  height: calc(100% - 85px);
  width: calc(100% - 20px);
  border: 2px dashed rgba(0, 0, 0, 0.05);
  border-radius: 7px;
  margin: 10px;
  font-size: 25px;
  color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.025);

  // Out of hit-testing in *both* states, not just while transparent. This
  // overlay appears and vanishes mid-drag, and dragHelper locates the drag
  // edges by counting balanced dragenter/dragleave pairs (see the dragDepth
  // comment in utils/DragHelper) - so a hit-testable overlay that materialises
  // under the pointer would be counted as another element crossed.
  pointer-events: none;

  // visibility rather than opacity alone, so the prompt leaves the
  // accessibility tree when it is not showing - at opacity 0 it stays there and
  // is announced whether or not anything is being dragged.
  //
  // The fade survives because visibility interpolates discretely with a special
  // rule: on the way to hidden it stays visible for the whole duration and snaps
  // at the end, and on the way back it turns visible immediately. So the
  // fade-out plays in full and the fade-in starts at once.
  transition:
    opacity 0.25s,
    visibility 0.25s;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  visibility: ${(p) => (p.$visible ? 'visible' : 'hidden')};
`;

const Icon = styled(LoginOutlined)`
  transform: rotate(90deg);
  font-size: 92px;
  opacity: 0.4;
`;

export interface DropIndicatorProps {
  visible: boolean;
  /**
   * Present so callers can adjust the geometry with
   * `styled(DropIndicator)` - the drawers differ only in their vertical
   * offsets, which depend on how much chrome sits above them.
   */
  className?: string;
}

/**
 * The dashed "drop files here" overlay shown while a file is being dragged
 * over a drawer.
 */
function DropIndicator({ visible, className }: DropIndicatorProps) {
  return (
    <Container className={className} $visible={visible}>
      <Icon />
      <br />
      DROP FILES HERE
    </Container>
  );
}

export default DropIndicator;
